import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Collection, useProductsContext} from '../Context/Products/ProductsContext';
import {Common, Creator, CurrencyAdapter, PricingEngineFactory} from '@taopix/taopix-core';
import {ProductsActions} from '../Context/Products/ProductsActionTypes';
import {Theme, ThemeName, Heading} from '@taopix/taopix-design-system';
import useLangCode from '../../Hooks/useLangCode';

export type Layout = {
  code: string;
  description: string;
  filterCode: string;
  filterName: string;
  moreInformationURL: string;
  name: string;
  previewImage: string;
  previewImageDevicePixelRatio: number;
  thumbnailImage: string;
  thumbnailImageDevicePixelRatio: number;
  upgradeLayoutImageDevicePixelRatio: number;
  price: any;
  defaultPages: number;
};

export type CreateProjectResponse = {
  brandcode: string;
  collectioncode: string;
  collectionname: string;
  collectiontype: number;
  designurl: string;
  groupcode: string;
  groupdata: string;
  layoutname: string;
  productcode: string;
  projectname: string;
  projectref: string;
  result: number;
  resultmessage: string;
  userdata: string;
};

const Products = () => {
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const {state, dispatch} = useProductsContext();
  const {collectionCode, groupCode} = useParams();
  const langCode = useLangCode();
  const navigate = useNavigate();

  const collection = useMemo(() => {
    return state.selector.collections.find(collection => {
      return collectionCode === collection.collectionCode;
    });
  }, [collectionCode]) as Collection;

  if (!collection) {
    navigate(`/products/${groupCode}?l=${langCode}`);
  }

  const getCreatorConfig = useCallback(
    (layout: Layout) => {
      return {
        api: {
          ...(window as any).config,
        },
        assets: [],
        callbackURL: state.urls.callbackUrl,
        groupCode: groupCode as string,
        collectionCode: collection.collectionCode,
        layoutCode: layout.code,
        extraData: btoa(JSON.stringify([])),
        settings: state.productTheming,
      };
    },
    [state, groupCode, collection]
  );

  useEffect(() => {
    if (!collection) {
      return;
    }

    dispatch(ProductsActions.setLoading(true));

    // TODO - Change this to axios
    fetch(`${window.config.baseURL}/api/product/layouts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        collectionUniqueRef: collection.collectionUniqueRef,
        groupCode,
        collectionCode: collection.collectionCode,
      }),
    })
      .then(response => {
        response.json().then(payload => {
          const formatted = payload.map((layout: Layout) => {
            return {
              ...layout,
              price: Object.keys(layout.price).reduce((accumulator, current) => {
                if (!accumulator[current.toLocaleLowerCase()]) {
                  accumulator[current.toLocaleLowerCase()] = layout.price[current];
                }

                return accumulator;
              }, {}),
            };
          });
          setLayouts(formatted);
        });
      })
      .finally(() => dispatch(ProductsActions.setLoading(false)));
  }, [collection]);

  const onSelectLayout = useCallback(
    async (layout: Layout) => {
      if (!state.productTheming.enabled) {
        return;
      }

      dispatch(ProductsActions.setLoading(true));

      const data = await Creator.createProject(getCreatorConfig(layout));
      if (0 === data.result) {
        window.location.href = data.designurl;
      } else {
        dispatch(ProductsActions.setLoading(false));
      }
    },
    [collection, state]
  );

  const getThumbnail = useCallback(
    (product: Layout) => {
      const thumb = `${state.urls.serverURL}${state.urls.thumbnailPath}/products/${collection.collectionCode}/${collection.thumbpath}/${product.previewImage}`;
      if ('' === product.previewImage) {
        return 'https://placehold.co/100x100';
      }
      return thumb;
    },
    [collection, state.urls]
  );

  const getProductPrice = useCallback(
    (product: Layout) => {
      // console.log(product.price);
      const engine = PricingEngineFactory.create(Number(collection.type) as any);
      const pricingEngineInstance = new engine(
        CurrencyAdapter(state.currency),
        0,
        product.price,
        false
      );

      const projectPrice = pricingEngineInstance.calculateProjectPrice(
        product.defaultPages,
        [],
        0,
        0
      );
      // console.log(projectPrice);
      return engine.formatCurrencyNumber(projectPrice, CurrencyAdapter(state.currency));
    },
    [collection, state]
  );

  return (
    <section className="selectCollectionStep-CollectionGrid grid-cols-1 grid min500:grid-cols-2 min800:grid-cols-3 min1100:grid-cols-5 gap-lg mt-md">
      {layouts.map((product, index) => {
        return (
          <Theme
            element="section"
            name={ThemeName.Section}
            id={`selectCollectionStep-collectionPanel-${index}`}
            key={`selectCollectionStep-collectionPanel-${index}`}
            className="selectCollectionStep-CollectionPanel group/collectionPanel flex flex-col cursor-pointer hover:outline hover:outline-[2px] hover:outline-themeAccentColour20 outline-offset-[-2px]"
            onClick={event => {
              event.stopPropagation();
              onSelectLayout(product);
            }}
          >
            <Theme
              name={ThemeName.Container}
              id={`selectCollectionStep-collectionPanelImageContainer-${index}`}
              allowBorder={false}
              allowCorners={false}
              className="w-full flex aspect-square relative"
            >
              {/* <div className="absolute top-10 right-10 inline-flex items-center rounded-md bg-yellow-50 p-10 text-md font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                From {getProductPrice(product)}
              </div> */}
              <img
                id={`selectCollectionStep-collectionPanelImage-${index}`}
                src={getThumbnail(product)}
                alt={`Collection ${index}`}
                className="object-cover w-full"
              />
            </Theme>
            <div
              id={`selectCollectionStep-collectionPanelNameContainer-${index}`}
              className="flex p-sm items-center"
            >
              <Heading
                id={`selectCollectionStep-collectionPanelName-${index}`}
                size={5}
                className="flex-1 flex-row"
              >
                <p>{Common.getCurrentLocaleString(product.name, true)}</p>
                <p className="font-bold">From {getProductPrice(product)}</p>
              </Heading>
            </div>
          </Theme>
        );
      })}
    </section>
  );
};

export default Products;
